import { getChannel, getDocument, getPlaceId } from '@lib/server';
import type { Aggregator, Place, SocialWithContact } from '@types';

export async function getSocialUIBlockData(domain: string, venue: string, show: { value: keyof SocialWithContact }[]) {
  const channel = await getChannel(domain);

  if (!channel?.channelId) return {};

  const template = (prefix: '.default.social' | '.default.contact', id: keyof SocialWithContact) =>
    `fields${venue ? prefix : ''}.${id}`;

  let fields;
  const select = [
    template('.default.social', 'facebook'),
    template('.default.social', 'instagram'),
    template('.default.social', 'twitter'),
    template('.default.social', 'tikTok'),
    template('.default.social', 'youTube'),
    template('.default.social', 'googlePlace'),
    template('.default.social', 'tripadvisor'),
    template('.default.social', 'designMyNight'),
    template('.default.social', 'linkedIn'),
    template('.default.contact', 'emailAddress'),
    template('.default.contact', 'phoneNumber'),
    'id',
  ].filter(
    (field) =>
      // don't filter out the id field
      field === 'id' ||
      // if show is empty, show all fields
      !show?.length ||
      // only show fields that are in the show array
      show?.some(
        (s) =>
          s.value ===
          field
            // remove the prefix from the field name at venue level
            .replace('fields.default.social.', '')
            .replace('fields.default.contact.', '')
            // remove the prefix from the field name at brand level
            .replace('fields.', '')
            .replace('fields.', ''),
      ),
  );

  if (venue) {
    const { placeId } = await getPlaceId(channel, venue);
    const venueData = await getDocument<Place>('place', placeId || '', {
      depth: 0,
      select,
      tags: [channel.channelId],
      visibilityId: placeId,
    });

    fields = {
      ...venueData?.fields?.default?.social,
      ...venueData?.fields?.default?.contact,
    } as SocialWithContact;
  } else {
    const data = await getDocument<Aggregator>('aggregator', channel.micrositeData[0].id, {
      depth: 0,
      select,
      tags: [channel.channelId],
    });

    fields = data.fields as SocialWithContact;
  }

  if (!fields) return {};

  return { ...fields } as const;
}
